import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MessageCircle, MapPin, Clock, Loader2, Send, Globe, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { treatments } from '../mockData';
import publicService from '../api/publicService';
import { motion, AnimatePresence } from 'framer-motion';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    treatment: '',
    message: ''
  });

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error("Validation Error", { description: "Please enter your full name." });
      return;
    }
    if (!formData.email?.trim() || !validateEmail(formData.email)) {
      toast.error("Validation Error", { description: "Please enter a valid email address." });
      return;
    }
    if (!formData.phone?.trim()) {
      toast.error("Validation Error", { description: "Please enter your phone number." });
      return;
    }
    if (!formData.country?.trim()) {
      toast.error("Validation Error", { description: "Please enter your country." });
      return;
    }
    if (!formData.treatment) {
      toast.error("Validation Error", { description: "Please select a treatment interest." });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        treatment_interest: formData.treatment,
        message: formData.message
      };

      await publicService.submitContactRequest(payload);

      toast.success("Consultation Request Received!", {
        description: "Our team will contact you within 24 hours.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        country: '',
        treatment: '',
        message: ''
      });
    } catch (error) {
      toast.error("Submission failed", {
        description: error.message || "Failed to send request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919660706047?text=Hello, I would like to know more about medical tourism in India', '_blank');
  };

  const contactInfo = [
    { icon: Phone, title: "Phone", value: "+91 9660706047", sub: "24/7 Support Line", color: "bg-blue-50 text-blue-600" },
    { icon: Mail, title: "Email", value: "info@bharatheal.com", sub: "Response in 24h", color: "bg-emerald-50 text-emerald-600" },
    { icon: MapPin, title: "Address", value: "4th Floor, PN 3 and 9, Office No. 402, Shree GD Tower, Vaishali Nagar, Jaipur, Rajasthan, 302021, India", sub: "Global Headquarters", color: "bg-rose-50 text-rose-600" },
    { icon: Globe, title: "International", value: "Worldwide Support", sub: "Multiple Timezones", color: "bg-sky-50 text-sky-600" },
  ];

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      {/* Premium Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#EEFBFC] rounded-full blur-[120px] opacity-40 -translate-y-1/2 translate-x-1/4 -z-10" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-50 rounded-full blur-[100px] opacity-30 -translate-x-1/4 translate-y-1/4 -z-10" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#EEFBFC] text-[#1B615F] rounded-full text-sm font-bold mb-6 border border-[#1B615F]/10"
          >
            <Sparkles className="w-4 h-4" />
            <span>CONNECT WITH OUR EXPERTS</span>
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1F1F1F] mb-6 tracking-tight leading-tight">
            Start Your <span className="text-[#1B615F]">Medical Journey</span> With Us
          </h2>
          <p className="text-lg text-gray-500 leading-relaxed">
            We're here to provide the support and guidance you need for a successful healthcare experience in India. Reach out today for a free consultation.
          </p>
        </motion.div>

        {/* Contact Info Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24"
        >
          {contactInfo.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="bg-white border border-gray-100 p-8 rounded-[32px] shadow-sm hover:shadow-xl transition-all duration-500 group"
            >
              <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`}>
                <item.icon className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
              <p className="text-[#1B615F] font-bold text-sm mb-1">{item.value}</p>
              <p className="text-gray-400 text-xs">{item.sub}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="bg-white rounded-[60px] border border-gray-100 shadow-[0_30px_100px_-20px_rgba(0,0,0,0.06)] overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Side: Aesthetic Call to Action */}
            <div className="lg:w-1/3 bg-gradient-to-br from-[#1B615F] to-[#4FA1A0] p-12 lg:p-16 text-white relative flex flex-col justify-center">
              <div className="absolute top-0 left-0 w-full h-full">
                <motion.div
                  animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
                  transition={{ duration: 10, repeat: Infinity }}
                  className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-[80px]"
                />
                <motion.div
                  animate={{ scale: [1.1, 1, 1.1], opacity: [0.1, 0.15, 0.1] }}
                  transition={{ duration: 12, repeat: Infinity }}
                  className="absolute bottom-10 right-10 w-48 h-48 bg-sky-200 rounded-full blur-[100px]"
                />
              </div>

              <div className="relative z-10">
                <h3 className="text-3xl font-black mb-6">Immediate Support?</h3>
                <p className="text-white/80 mb-8 leading-relaxed">
                  Our dedicated care managers are available 24/7 to answer your questions and help you navigate your options.
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleWhatsApp}
                  className="bg-white text-[#1B615F] w-full py-5 rounded-2xl font-black flex items-center justify-center gap-3 shadow-xl hover:bg-gray-50 transition-colors"
                >
                  <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  Chat on WhatsApp
                </motion.button>

                <div className="mt-12 space-y-6">
                  <div className="flex items-center gap-4 text-white/90">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest opacity-60">Avg. Response</div>
                      <div className="font-bold">Under 15 Minutes</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-white/90">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <Star className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest opacity-60">Trust Score</div>
                      <div className="font-bold">4.9/5 Patients Rated</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Professional Form */}
            <div className="lg:w-2/3 p-12 lg:p-16">
              <div className="max-w-2xl mx-auto">
                <h3 className="text-3xl font-black text-[#1F1F1F] mb-10 flex items-center gap-4">
                  Request a Personalized Plan
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 overflow-hidden"><img loading="lazy" decoding="async" src={`https://i.pravatar.cc/100?u=${i + 10}`} alt="user" /></div>)}
                  </div>
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 ml-1">Full Name</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. David Smith"
                        className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] py-7 text-base transition-all h-[60px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 ml-1">Email Address</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="david@example.com"
                        className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] py-7 text-base transition-all h-[60px]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 ml-1">Phone Number</label>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+1 (555) 000-0000"
                        className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] py-7 text-base transition-all h-[60px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 ml-1">Country</label>
                      <Input
                        required
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        placeholder="United Kingdom"
                        className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] py-7 text-base transition-all h-[60px]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-1">Preferred Treatment</label>
                    <Select
                      value={formData.treatment}
                      onValueChange={(value) => setFormData({ ...formData, treatment: value })}
                      required
                    >
                      <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] py-7 text-base transition-all h-[60px]">
                        <SelectValue placeholder="What treatment are you interested in?" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl border-gray-100 shadow-2xl">
                        {treatments.map((treatment) => (
                          <SelectItem key={treatment.id} value={treatment.title} className="hover:bg-[#EEFBFC] cursor-pointer py-3 h-[50px] font-medium">
                            {treatment.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="other" className="hover:bg-[#EEFBFC] cursor-pointer py-3">Other Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-1">Additional Details</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Share any specific requirements or medical history..."
                      className="rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white focus:border-[#1B615F] min-h-[150px] text-base transition-all resize-none p-5"
                    />
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#1B615F] hover:bg-[#144947] text-white font-black py-8 text-xl rounded-2xl shadow-xl shadow-[#1B615F]/20 transition-all flex items-center justify-center gap-4 group h-[70px]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-6 w-6 animate-spin" />
                          PROCESSING...
                        </>
                      ) : (
                        <>
                          SEND SECURE REQUEST
                          <Send className="h-6 w-6 transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-2" />
                        </>
                      )}
                    </Button>
                  </motion.div>

                  <p className="text-xs text-gray-400 text-center leading-relaxed italic">
                    All medical data shared is encrypted and handled with strict confidentiality. Our team will perform a preliminary review and contact you shortly.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
